import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PAYROLL_RUN_STATUS_MAP,
  tafqit,
  type Branding,
  type CompanyProfile,
  type PayrollRunStatus,
  type TenantSettings,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { closePrintView } from '../../lib/printclose';
import { date, money, num } from '../../lib/format';
import { Logo } from '../../components/Logo';
import { PaperBackground, paperPageProps, useDocumentPaper } from '../../components/DocumentPaper';
import '../../styles/print.css';

/**
 * قسيمة العمولة المطبوعة — من عائلة مستندات الشركة نفسها.
 *
 * الترويسة والهوية هي ترويسة الفاتورة وكشف الحساب سواء: ما يصل يد
 * المندوب يبدو صادرًا من الجهة التي تصرف له، لا ورقةً من شاشة.
 *
 * وهي تفصيلٌ لا إعادةَ حساب: الأرقام المجمَّدة في الكشف هي المرجع،
 * والمستندات تُقرأ لتقول من أين جاءت — فإن اختلفت اليومَ عمّا جُمِّد قيل
 * الفارق ولم يُخفَ.
 */

interface Profile {
  tenant: { name: string; currency: string };
  branding: Branding;
  company: CompanyProfile;
  settings: TenantSettings;
}

interface SlipDoc {
  id: string;
  kind: 'PAYMENT' | 'INVOICE';
  number: string;
  at: string;
  amount: number;
  commission: number;
  method: string | null;
  accountName: string | null;
  invoiceNumber: string | null;
  dealTitle: string | null;
}

interface SlipData {
  run: {
    id: string;
    periodLabel: string;
    status: PayrollRunStatus;
    basis: 'COLLECTED' | 'INVOICED';
    basisLabel: string;
    targetPolicyLabel: string;
  };
  employee: {
    id: string;
    name: string;
    code: string | null;
    jobTitle: string | null;
    linked: boolean;
  };
  line: {
    basisAmount: number;
    target: number | null;
    rate: number;
    eligible: number;
    commission: number;
    documentCount: number;
    paid: boolean;
  };
  docsTotal: number;
  drift: number;
  deals: Array<{
    dealId: string | null;
    dealTitle: string | null;
    accountName: string | null;
    count: number;
    amount: number;
    commission: number;
  }>;
  docs: SlipDoc[];
}

export function PrintCommissionSlip() {
  const { runId = '', employeeId = '' } = useParams<{
    runId: string;
    employeeId: string;
  }>();

  /*
   * «me» بدل معرّف الموظف.
   *
   * المندوبُ يفتح قسيمتَه من «عمولاتي»، ولا يُرسَل معرّفُه في المسار:
   * الخادمُ يشتقّه من الجلسة، وإلّا قرأ كلُّ أحدٍ قسيمةَ غيره بتبديل رقمٍ
   * في العنوان. ومسارُه يمنع المسوّدةَ أيضًا — كشفٌ لم يُعتمد لا يُوعَد به.
   */
  const self = employeeId === 'me';
  const slip = useQuery({
    queryKey: ['commissions', 'slip', runId, employeeId],
    queryFn: () =>
      api<SlipData>(
        self
          ? `/commissions/mine/slip/${runId}`
          : `/commissions/runs/${runId}/slip/${employeeId}`,
      ),
  });
  const profile = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<Profile>('/settings'),
  });
  /* ورقةُ الشركة (ليتر هيد) إن رُفعت: تحلّ محلّ ترويسة النظام وتذييله. */
  const { paper, ready: paperReady } = useDocumentPaper('letterhead');

  const s = slip.data;
  const p = profile.data;

  if (slip.isError) {
    return <div className="print-root">تعذّر تحميل القسيمة.</div>;
  }
  if (!s || !p || !paperReady) {
    return <div className="print-root">جارٍ التحضير…</div>;
  }

  const { tenant, branding, company, settings } = p;
  const currency = settings.currencyLabel || 'ج.م';
  const collected = s.run.basis === 'COLLECTED';

  const summary: Array<[string, string]> = [
    [collected ? 'المحصَّل خلال الشهر' : 'المفوتر خلال الشهر', money(s.line.basisAmount, currency)],
    ['الهدف الشهري', s.line.target ? money(s.line.target, currency) : 'بلا هدف'],
    [`الخاضع للعمولة (${s.run.targetPolicyLabel})`, money(s.line.eligible, currency)],
    ['نسبة العمولة', `${num(s.line.rate)}٪`],
  ];

  return (
    <div
      className="print-root"
      style={{
        ['--doc-primary' as string]: branding.primary,
        ['--doc-ink' as string]: branding.ink,
      }}
    >
      <div className="print-toolbar no-print">
        <button className="btn btn-primary" onClick={() => window.print()}>
          طباعة أو حفظ PDF
        </button>
        <button className="btn" onClick={closePrintView}>
          إغلاق
        </button>
        <span className="small mute">
          اختر «حفظ كـ PDF» في نافذة الطباعة للحصول على ملف.
        </span>
      </div>

      <article {...paperPageProps(paper, 'print-page')}>
        <PaperBackground paper={paper} />
        <header className="doc-header">
          <div className="doc-brand">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="" className="doc-logo" />
            ) : (
              <Logo size={52} />
            )}
            <div>
              <div className="doc-company">{company.legalName || tenant.name}</div>
              {branding.documentHeader && (
                <div className="doc-tagline">{branding.documentHeader}</div>
              )}
              <div className="doc-company-meta">
                {company.address && <div>{company.address}</div>}
                <div className="doc-inline">
                  {company.phone && <span className="num">{company.phone}</span>}
                  {company.email && <span className="num">{company.email}</span>}
                </div>
              </div>
            </div>
          </div>

          <div className="doc-title-block">
            <div className="doc-kind">قسيمة عمولة</div>
            <div className="doc-number">{s.run.periodLabel}</div>
          </div>
        </header>

        <section className="doc-parties">
          <div className="doc-party">
            <div className="doc-label">المستحِقّ</div>
            <div className="doc-party-name">{s.employee.name}</div>
            {s.employee.jobTitle && <div>{s.employee.jobTitle}</div>}
            {s.employee.code && (
              <div>
                كود الموظف: <span className="num">{s.employee.code}</span>
              </div>
            )}
          </div>

          <div className="doc-dates">
            <div className="doc-date-row">
              <span className="doc-label">الفترة</span>
              <span>{s.run.periodLabel}</span>
            </div>
            <div className="doc-date-row">
              <span className="doc-label">أساس الاحتساب</span>
              <span>{s.run.basisLabel}</span>
            </div>
            <div className="doc-date-row">
              <span className="doc-label">حالة الكشف</span>
              <span>
                {PAYROLL_RUN_STATUS_MAP[s.run.status].label}
                {s.line.paid ? ' — نزلت بندًا في المرتّب' : ''}
              </span>
            </div>
          </div>
        </section>

        <table className="doc-table">
          <thead>
            <tr>
              <th>البيان</th>
              <th style={{ width: 170 }}>القيمة</th>
            </tr>
          </thead>
          <tbody>
            {summary.map(([label, value]) => (
              <tr key={label}>
                <td>{label}</td>
                <td className="num end">{value}</td>
              </tr>
            ))}
            <tr>
              <td className="strong">صافي العمولة المستحقّة</td>
              <td className="num end strong">{money(s.line.commission, currency)}</td>
            </tr>
          </tbody>
        </table>

        {/* من أيّ صفقة — التجميعُ قبل التفصيل: نظرةٌ ثمّ برهان. */}
        <h3 style={{ marginTop: 20, marginBottom: 8 }}>من أيّ صفقة</h3>
        <table className="doc-table">
          <thead>
            <tr>
              <th>الصفقة</th>
              <th>العميل</th>
              <th style={{ width: 80 }}>المستندات</th>
              <th style={{ width: 130 }}>{collected ? 'المحصَّل' : 'المفوتر'}</th>
              <th style={{ width: 120 }}>العمولة</th>
            </tr>
          </thead>
          <tbody>
            {s.deals.length === 0 && (
              <tr>
                <td colSpan={5} className="center">
                  لا مستندات في هذا الشهر.
                </td>
              </tr>
            )}
            {s.deals.map((g, i) => (
              <tr key={g.dealId ?? `x${i}`}>
                <td>{g.dealTitle ?? 'بلا صفقة مرتبطة — فاتورة مباشرة'}</td>
                <td>{g.accountName ?? '—'}</td>
                <td className="num center">{num(g.count)}</td>
                <td className="num end">{money(g.amount, currency)}</td>
                <td className="num end">{money(g.commission, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <h3 style={{ marginTop: 20, marginBottom: 8 }}>
          {collected ? 'التحصيلات' : 'الفواتير'}
        </h3>
        <table className="doc-table">
          <thead>
            <tr>
              <th style={{ width: 130 }}>{collected ? 'الدفعة' : 'الفاتورة'}</th>
              <th style={{ width: 100 }}>التاريخ</th>
              <th>العميل</th>
              {collected && <th style={{ width: 120 }}>عن فاتورة</th>}
              {collected && <th style={{ width: 90 }}>الطريقة</th>}
              <th style={{ width: 120 }}>المبلغ</th>
              <th style={{ width: 110 }}>العمولة</th>
            </tr>
          </thead>
          <tbody>
            {s.docs.length === 0 && (
              <tr>
                <td colSpan={collected ? 7 : 5} className="center">
                  لا مستندات في هذا الشهر.
                </td>
              </tr>
            )}
            {s.docs.map((doc) => (
              <tr key={doc.id}>
                <td className="num">{doc.number}</td>
                <td className="num center">{date(doc.at)}</td>
                <td>{doc.accountName ?? '—'}</td>
                {collected && <td className="num">{doc.invoiceNumber ?? '—'}</td>}
                {collected && <td>{doc.method ?? '—'}</td>}
                <td className="num end">{money(doc.amount, currency)}</td>
                <td className="num end">{money(doc.commission, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="doc-words" style={{ marginTop: 16 }}>
          <div className="doc-label">العمولة بالحروف</div>
          <div className="doc-words-text">
            {s.line.commission > 0
              ? tafqit(s.line.commission, tenant.currency)
              : 'لا عمولة مستحقّة عن هذه الفترة.'}
          </div>
        </div>

        {Math.abs(s.drift) > 0.01 && (
          <p className="small" style={{ marginTop: 12, color: '#8a5a00' }}>
            تنبيه: مستندات الشهر تجمع اليوم {money(s.docsTotal, currency)} والمحفوظ
            في الكشف {money(s.line.basisAmount, currency)} — فارقٌ قدره{' '}
            {money(Math.abs(s.drift), currency)} نشأ عن تعديلٍ بعد إنشاء الكشف.
            الرقم المعتمد هو المرجع.
          </p>
        )}

        <p className="small" style={{ marginTop: 16, color: '#666' }}>
          العمولة تُحتسب على مجموع الشهر ثمّ تمرّ على سياسة الهدف، فما بجانب كلّ
          مستندٍ هنا حصّته من العمولة بنسبة مبلغه — لا عمولةٌ حُسبت له وحده.
          وأرقام هذه القسيمة محفوظة كما حُسبت في الكشف ولا تتغيّر بأثرٍ رجعي.
        </p>
      </article>
    </div>
  );
}
